import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Person } from '@domain/entities/Person';
import {
  FIELD_LABEL,
  MAX_CORRECTION_LENGTH,
  REPORTABLE_FIELDS,
  type ConfirmableField,
} from '@domain/entities/DataConfirmation';
import { PARENT_ROLES, SHIRT_SIZE_LABEL, type ParentRole } from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import { Banner, Button, Modal, cx, inputClass } from '../../design-system';
import { formatDayWithYear, reasonFor } from '../../format';
import { newestLive, type LiveBalance } from '../../balancePeriod';
import { useLiveBalances } from '../../hooks/useLiveBalances';
import { useToast } from '../../providers/ToastProvider';
import { useRuntimeConfig } from '../../providers/RuntimeConfigProvider';
import styles from './ConfirmDataModal.module.css';

/**
 * «Confirma tus datos», la primera vez que alguien entra.
 *
 * El padrón entró de un formulario de Google contestado por 338 personas y se
 * cargó de una vez. Se sabe que trae basura: dos fichas dicen «Servicios
 * profesionales» donde va la fecha de ingreso, el organigrama se dedujo de un
 * campo de texto libre con 99 grafías para unos cuarenta jefes, y 38 personas no
 * tienen quien les firme. Revisarlo desde RR.HH. es abrir 338 fichas; cada quien
 * abre la suya en veinte segundos y ya sabe la respuesta.
 *
 * **No se puede cerrar, y aun así nadie queda encerrado.** Las dos salidas están
 * dentro —«Todo está correcto» y «Algo está mal»— y las dos terminan igual de
 * rápido. Una ✕ aquí sería la tercera salida: la que deja la ficha sin revisar y
 * a la persona pidiendo días sobre datos que nunca miró.
 *
 * **Sale una sola vez.** El documento lleva el id de la persona, así que «¿ya
 * contestó?» es leer un documento y no una consulta, y contestar dos veces desde
 * dos pestañas no crea dos reportes.
 */
export const ConfirmDataModal = ({
  person,
  onDone,
  onCancel,
}: {
  readonly person: Person;
  readonly onDone: () => void;
  /**
   * Presente sólo cuando la abre la propia persona desde su perfil.
   *
   * Y ésa es toda la diferencia entre las dos aperturas, que por lo demás son la
   * misma tarjeta: **la obligatoria no se puede cerrar y la voluntaria sí**.
   * Encerrar a alguien que entró por su cuenta a corregir un dato sería un
   * castigo por colaborar; encerrar a quien nunca ha revisado su ficha es lo que
   * hace que la revise. Sin este prop la tarjeta se comporta como siempre.
   */
  readonly onCancel?: () => void;
}): ReactNode => {
  const voluntaria = onCancel !== undefined;
  const config = useRuntimeConfig();
  const toast = useToast();

  /* Quien entra por su cuenta ya sabe que algo está mal: se le ahorra el clic de
     «Algo está mal» y se le abren las casillas de una vez. */
  const [fixing, setFixing] = useState(voluntaria);
  const [marked, setMarked] = useState<ReadonlySet<ConfirmableField>>(new Set());
  const [values, setValues] = useState<Readonly<Partial<Record<ConfirmableField, string>>>>({});
  const [sending, setSending] = useState(false);
  /*
   * La respuesta a «¿eres papá o mamá?», que **no es una corrección**: se guarda
   * en la ficha. Arranca en lo que ya tenga, así que quien ya lo contestó ve su
   * respuesta marcada y no vuelve a contestarla.
   */
  const [parentRole, setParentRole] = useState<ParentRole | null>(person.parentRole);

  const areas = useQuery({
    queryKey: ['confirm', 'areas'],
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      return result.ok ? result.value : [];
    },
  });

  const subAreas = useQuery({
    queryKey: ['confirm', 'subAreas', person.areaId],
    queryFn: async () => {
      const result = await getServices().organization.listSubAreas(person.areaId);
      return result.ok ? result.value : [];
    },
  });

  /**
   * Quién le firma, resuelto a nombres.
   *
   * `leadersOf` pide el área además de la persona porque la regla de
   * `personLeaders` autoriza el listado por `inScope(resource.data.areaId)` y una
   * consulta que no filtre ese campo no la cumple. Va el área de la persona, que
   * aquí es la misma de quien mira porque sólo se mira a sí misma.
   */
  const leaders = useQuery({
    queryKey: ['confirm', 'leaders', person.id],
    queryFn: async () => {
      const links = await getServices().leaders.leadersOf(person.id, person.areaId);
      if (!links.ok || links.value.length === 0) return [];
      const people = await getServices().people.getMany(
        links.value.map((link) => link.leaderPersonId),
      );
      return people.ok ? people.value.map((leader) => leader.displayName) : [];
    },
  });

  /**
   * El saldo, pedido igual que en Inicio.
   *
   * Tiene que ser **el mismo número** que la persona ve en su pantalla: si aquí
   * saliera otro, confirmar este dejaría confirmado algo que nadie le enseñó. Por
   * eso sale de `useLiveBalances`, lo mismo que usa el resumen de Inicio: un
   * periodo por tramo, cada uno con su fecha.
   */
  const balance = useLiveBalances({
    scope: 'confirm',
    personId: person.id,
    hireDate: person.hireDate,
    expiryMonths: config.rules.expiryMonths,
  });

  const shown = useMemo((): Readonly<Record<ConfirmableField, string>> => {
    const sinRegistrar = 'Sin registrar';
    const area = (areas.data ?? []).find((item) => item.id === person.areaId);
    const subArea = (subAreas.data ?? []).find((item) => item.id === person.subAreaId);
    const position = config.catalogs.positions.find((item) => item.id === person.positionId);
    const nombres = leaders.data ?? [];

    return {
      nombre: person.displayName,
      correo: person.email.length > 0 ? person.email : sinRegistrar,
      nacimiento:
        person.birthDate === null ? sinRegistrar : formatDayWithYear(person.birthDate),
      fechaIngreso: person.hireDate === null ? sinRegistrar : formatDayWithYear(person.hireDate),
      area: area?.name ?? sinRegistrar,
      /* «Todavía no se define» y no «Sin registrar»: en seis de las ocho áreas
         nadie ha creado sub áreas aún, y eso no es un dato que falte en su ficha
         sino una tarea de su administrador. Pedirle que lo corrija sería pedirle
         que arregle algo que no está en su mano. */
      subArea: subArea?.name ?? 'Todavía no se define en tu área',
      puesto: position?.title ?? sinRegistrar,
      lider: nombres.length > 0 ? nombres.join(', ') : sinRegistrar,
      saldo:
        balance.data === undefined || balance.data.length === 0
          ? sinRegistrar
          : describeLiveBalances(balance.data),
      /* Los tres últimos salen del libro de eventos y no del formulario, así que
         para las 322 fichas cargadas el 1 de septiembre ésta es la primera vez
         que alguien los ve. Es justo lo que la tarjeta existe para atrapar. */
      talla: person.shirtSize === null ? sinRegistrar : SHIRT_SIZE_LABEL[person.shirtSize],
      /* Ya no se pinta como fila —se pregunta abajo— pero el registro cubre el
         catálogo entero: `FIELD_LABEL` y la bandeja siguen conociéndolo, y en
         producción hay una corrección guardada con esta llave. */
      papaMama: '',
    };
  }, [
    areas.data,
    subAreas.data,
    leaders.data,
    balance.data,
    config.catalogs.positions,
    person,
  ]);

  const cargando =
    areas.isPending || subAreas.isPending || leaders.isPending || balance.isPending;

  /** Lo que de verdad se manda: marcada y con valor. Ver `cleanCorrections`. */
  const corrections = useMemo(
    () =>
      REPORTABLE_FIELDS.filter((field) => marked.has(field))
        .map((field) => ({
          field,
          shown: shown[field],
          correct: (values[field] ?? '').trim(),
        }))
        .filter((item) => item.correct.length > 0),
    [marked, values, shown],
  );

  const submit = async (payload: typeof corrections): Promise<void> => {
    setSending(true);
    const result = await getServices().dataConfirmations.submit({
      corrections: payload,
      /* Se manda siempre, también cuando no cambió: `undefined` sería «no lo
         tocó» y aquí siempre lo tocó, aunque sea para dejarlo igual. */
      parentRole,
    });
    setSending(false);

    if (!result.ok) {
      toast.bad(`No pudimos guardar tu respuesta.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(
      payload.length === 0
        ? 'Gracias por confirmar tus datos.'
        : 'Lo reportado ya le llegó a quien administra tu área.',
    );
    onDone();
  };

  const toggle = (field: ConfirmableField): void => {
    setMarked((current) => {
      const next = new Set(current);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  return (
    <Modal
      title={voluntaria ? 'Corrige tus datos' : 'Confirma tus datos'}
      dismissible={voluntaria}
      /* En la obligatoria nunca se llama —no hay ✕, ni Escape, ni velo— y el tipo
         lo exige igual. */
      onClose={onCancel ?? (() => undefined)}
      footer={
        fixing ? (
          <>
            <Button
              variant="ghost"
              disabled={sending}
              onClick={() => {
                /* En la voluntaria «Volver» no tiene a dónde volver: se entró ya
                   marcando. Cierra, que es la salida que esa persona espera. */
                if (voluntaria) onCancel?.();
                else setFixing(false);
              }}
            >
              {voluntaria ? 'Cancelar' : 'Volver'}
            </Button>
            <Button
              variant="primary"
              busy={sending}
              disabled={corrections.length === 0}
              onClick={() => {
                void submit(corrections);
              }}
            >
              {corrections.length === 0
                ? 'Escribe lo correcto'
                : `Enviar ${String(corrections.length)} ${corrections.length === 1 ? 'corrección' : 'correcciones'}`}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              disabled={sending || cargando}
              onClick={() => {
                setFixing(true);
              }}
            >
              Algo está mal
            </Button>
            <Button
              variant="primary"
              busy={sending}
              disabled={cargando}
              onClick={() => {
                void submit([]);
              }}
            >
              Todo está correcto
            </Button>
          </>
        )
      }
    >
      <div className={styles.body}>
        <p className={styles.lead}>
          {fixing
            ? 'Marca lo que no coincida y escribe cómo debería decir. Lo revisa quien administra tu área.'
            : 'Así entraron tus datos al portal. Revísalos antes de pedir días: de la fecha de ingreso salen tus días de vacaciones, y de tu jefe directo quién te los firma.'}
        </p>

        {cargando ? (
          <p className={styles.lead}>Cargando tu ficha…</p>
        ) : (
          <ul className={styles.rows}>
            {REPORTABLE_FIELDS.map((field) => {
              const isMarked = marked.has(field);
              return (
                <li key={field} className={cx(styles.row, isMarked && styles.rowMarked)}>
                  <div className={styles.rowHead}>
                    <span className={styles.label}>{FIELD_LABEL[field]}</span>
                    <span className={styles.value}>{shown[field]}</span>
                  </div>

                  {fixing && (
                    <label className={styles.mark}>
                      <input
                        type="checkbox"
                        checked={isMarked}
                        onChange={() => {
                          toggle(field);
                        }}
                      />
                      <span>Está mal</span>
                    </label>
                  )}

                  {fixing && isMarked && (
                    <input
                      className={cx(inputClass(), styles.fix)}
                      maxLength={MAX_CORRECTION_LENGTH}
                      placeholder={`Lo correcto es…`}
                      aria-label={`Lo correcto en ${FIELD_LABEL[field]}`}
                      value={values[field] ?? ''}
                      onChange={(event) => {
                        setValues((current) => ({ ...current, [field]: event.target.value }));
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/*
          La única pregunta de la tarjeta que la persona **contesta** en vez de
          reportar, y por eso vive fuera de la lista.

          Antes era una fila más —«Papá o mamá — Sin registrar»— con su casilla de
          «Está mal» y su renglón de texto. Alguien la leyó y escribió el nombre de
          su madre: entendió que le pedían quién es su mamá, no si lo es. Un rótulo
          que hay que interpretar produce respuestas a otra pregunta, y no hay tipo
          ni prueba que lo vea — se descubrió leyendo lo que la gente contestó.

          Se guarda en su ficha porque nadie lo sabe mejor y porque no gobierna
          nada: ni saldo, ni aprobaciones, ni permisos. Los otros diez sí, y por eso
          ésos se reportan.
        */}
        {!cargando && (
          <div className={styles.ask}>
            <p className={styles.askTitle}>¿Eres papá o mamá?</p>
            <div className={styles.askOptions}>
              {PARENT_ROLES.map((rol) => (
                <label key={rol} className={styles.askOption}>
                  <input
                    type="checkbox"
                    checked={parentRole === rol}
                    onChange={() => {
                      /* Una o ninguna: marcar una apaga la otra, y volver a
                         marcar la que ya estaba la apaga. No hay un tercer
                         estado que guardar ni una pregunta que forzar. */
                      setParentRole((actual) => (actual === rol ? null : rol));
                    }}
                  />
                  <span>{rol === 'papa' ? 'Sí, soy papá' : 'Sí, soy mamá'}</span>
                </label>
              ))}
            </div>
            <p className={styles.askHint}>
              Marca una, o ninguna si no es tu caso. Sirve para felicitarte en su día.
            </p>
          </div>
        )}

        {fixing && (
          <Banner tone="info">
            Lo que marques sin escribir el valor correcto no se manda: sin él, quien lo reciba
            tendría que escribirte para preguntarte cuál es.
          </Banner>
        )}
      </div>
    </Modal>
  );
};

/**
 * «20 días disponibles de 22», y delante lo que quede de periodos anteriores con
 * su fecha: «6 días del 2025, hasta el 15 sep 2027; 20 días disponibles de 22».
 */
const describeLiveBalances = (lives: readonly LiveBalance[]): string => {
  const current = newestLive(lives);
  const older = lives
    .filter((live) => live !== current)
    .map(
      (live) =>
        `${String(live.available)} ${live.available === 1 ? 'día' : 'días'} del ${String(live.year)}, hasta el ${formatDayWithYear(live.lastValidDay)}`,
    );
  const main =
    current === null
      ? []
      : [`${String(current.available)} días disponibles de ${String(current.balance.entitledDays)}`];
  return [...older, ...main].join('; ');
};
